import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ScoreBreakdown } from '@/components/ScoreBreakdown/ScoreBreakdown';
import type { ScoreBreakdownData } from '@/components/ScoreBreakdown/ScoreBreakdown';
import { getAllColumns } from '@/components/Table/columns/index';
import { userAppContext, searchContext } from '@/context/Context';
import {
  fetchGdaEvidencesWithTimeline,
  filterYearStatsToRange,
  getRowAssociationTimelineParams,
  type HistoricalScoreYearPoint,
} from '@/services/evidenceTimelineService';
import { useMappingStackStore } from '@/store';
import type { MappingLevelContent } from '@/types/mappingDrillDown';
import { buildNumPmidMappingConfig } from '@/utils/evidenceTimelineMapping';

import { HistoricalScoreLineChart } from './HistoricalScoreLineChart';
import { ScoreHistoryPanel } from './ScoreHistoryPanel';
import type { ScoreHistoryPanelState } from '../utils/searchResultsHelpers';

function toPlainText(node: unknown): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toPlainText).join('');
  if (typeof node === 'object' && node !== null && 'props' in (node as any)) {
    return toPlainText((node as { props?: { children?: unknown } }).props?.children);
  }
  return '';
}

type HistoricalScoreTabContentProps = {
  row: any;
  source: string;
  tab: string;
  onClosePanel?: () => void;
  ensureTablesViewForDrilldown?: () => void;
};

function emptyPanelState(): ScoreHistoryPanelState {
  return {
    selectedYear: null,
    selectedYearFrom: null,
    selectedYearTo: null,
    yearInput: '',
    yearFromInput: '',
    yearToInput: '',
    tab: undefined,
  };
}

export function HistoricalScoreTabContent({
  row,
  source,
  tab,
  onClosePanel,
  ensureTablesViewForDrilldown,
}: HistoricalScoreTabContentProps) {
  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (e: unknown) => void;
    groups?: string[];
    isAuthenticated?: boolean;
  };
  const authorization = userCtx?.authorization;
  const setAuthErr = userCtx?.setAuthError ?? (() => {});

  const {
    view = '',
    tab: contextTab,
    searchItems = [],
    setMappingContent,
  } = (searchContext() ?? {}) as {
    view?: string;
    tab?: string;
    searchItems?: Array<{ chemID?: string; chemPrefName?: string }>;
    setMappingContent?: (content: Record<string, unknown> | (() => Record<string, unknown>)) => void;
  };
  const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';

  const [panel, setPanel] = useState<ScoreHistoryPanelState>(() => emptyPanelState());
  const [breakdown, setBreakdown] = useState<ScoreBreakdownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noBreakdownInResponse, setNoBreakdownInResponse] = useState(false);
  const [scoreYearRange, setScoreYearRange] = useState<{ min: number; max: number } | null>(null);
  const [chartPoints, setChartPoints] = useState<HistoricalScoreYearPoint[]>([]);

  const rowKey = useMemo(() => {
    const assoc = getRowAssociationTimelineParams(row, tab);
    return assoc
      ? `${assoc.apiTab}:${assoc.diseaseFilter}:${assoc.geneSymbol ?? assoc.variantName ?? ''}`
      : '';
  }, [row, tab]);

  useEffect(() => {
    setPanel(emptyPanelState());
    setBreakdown(null);
    setError(null);
    setLoading(false);
    setNoBreakdownInResponse(false);
    setScoreYearRange(null);
    setChartPoints([]);
  }, [rowKey]);

  const assoc = useMemo(() => getRowAssociationTimelineParams(row, tab), [row, tab]);

  const requestSeq = useRef(0);

  const runFetch = useCallback(
    async (minPmidYear: number | null, maxPmidYear: number | null) => {
      if (!assoc || !source) {
        setError('Could not resolve gene/variant and disease for this row.');
        setBreakdown(null);
        return;
      }

      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      setNoBreakdownInResponse(false);
      setChartPoints([]);

      const yMin = minPmidYear != null && Number.isFinite(minPmidYear) ? Math.trunc(minPmidYear) : null;
      const yMax = maxPmidYear != null && Number.isFinite(maxPmidYear) ? Math.trunc(maxPmidYear) : null;
      const isMultiYearRange = yMin != null && yMax != null && yMin < yMax;

      try {
        const result = await fetchGdaEvidencesWithTimeline({
          source,
          diseaseFilter: assoc.diseaseFilter,
          page_number: 0,
          page_size: 50,
          authorization,
          setAuthError: setAuthErr,
          tab: assoc.apiTab,
          geneSymbol: assoc.geneSymbol,
          variantName: assoc.variantName,
          minPmidYear,
          maxPmidYear,
        });

        if (seq !== requestSeq.current) return;

        const gs = result.globalScore ?? null;
        setBreakdown(gs);
        setNoBreakdownInResponse(!gs);

        if (yMin != null && yMax != null && yMin <= yMax) {
          setScoreYearRange({ min: yMin, max: yMax });
        } else {
          setScoreYearRange(null);
        }

        if (isMultiYearRange) {
          setChartPoints(filterYearStatsToRange(result.yearStats ?? [], yMin, yMax));
        } else {
          setChartPoints([]);
        }
      } catch {
        if (seq !== requestSeq.current) return;
        setBreakdown(null);
        setNoBreakdownInResponse(false);
        setScoreYearRange(null);
        setChartPoints([]);
        setError('Could not load historical score.');
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
        }
      }
    },
    [assoc, authorization, setAuthErr, source],
  );

  const handleSelectionCommit = useCallback(
    (detail: { year?: number | null; yearFrom?: number | null; yearTo?: number | null }) => {
      const y = detail.year;
      const yf = detail.yearFrom;
      const yt = detail.yearTo;

      const hasRange =
        typeof yf === 'number' &&
        Number.isFinite(yf) &&
        typeof yt === 'number' &&
        Number.isFinite(yt) &&
        yf <= yt;

      if (hasRange) {
        void runFetch(Math.trunc(yf!), Math.trunc(yt!));
        return;
      }

      if (typeof y === 'number' && Number.isFinite(y)) {
        const yi = Math.trunc(y);
        void runFetch(yi, yi);
        return;
      }

      void runFetch(null, null);
    },
    [runFetch],
  );

  const gridTab = String(contextTab ?? tab ?? '');

  const numPmidColumnForPublications = getAllColumns({ isEvidence: true, tab: gridTab }).find(
    (c: { name?: string }) => c.name === 'numPMIDsupportingAssociation',
  );

  const handleViewPublications = useCallback(() => {
    if (!scoreYearRange || !row) return;
    const config = buildNumPmidMappingConfig(
      row as Record<string, unknown>,
      String(view),
      gridTab,
      searchItems,
    );
    if (!config) return;

    const mappingPayload: MappingLevelContent = {
      ...config,
      selectedPmYearFrom: scoreYearRange.min,
      selectedPmYearTo: scoreYearRange.max,
      selectedReferenceType: 'ALL',
    };

    const headerText = toPlainText(numPmidColumnForPublications?.header).trim() || 'Publications';
    const yearLabel =
      scoreYearRange.min === scoreYearRange.max
        ? String(scoreYearRange.min)
        : `${scoreYearRange.min}–${scoreYearRange.max}`;

    const closePanelSoon = () => queueMicrotask(() => onClosePanel?.());

    if (useDrillDownMapping) {
      ensureTablesViewForDrilldown?.();
      window.setTimeout(() => {
        const { stack, resetStack, pushLevel } = useMappingStackStore.getState();
        const needsBase = stack.length === 0 || !stack[0]?.isBase;
        if (needsBase) {
          resetStack({
            id: `base-${gridTab || 'GDA'}`,
            label: 'Results',
            isBase: true,
            searchItems: (searchItems as any[]) ?? [],
            panelTab: gridTab || String(tab ?? 'GDA'),
            isEvidence: false,
          });
        }
        pushLevel({
          id: `level-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          label: `${headerText} · ${yearLabel}`,
          labelNode: numPmidColumnForPublications?.header ?? headerText,
          isBase: false,
          mappingContent: mappingPayload,
        });
        closePanelSoon();
      }, 0);
      return;
    }

    if (typeof setMappingContent === 'function') {
      setMappingContent(() => ({
        isActive: true,
        ...mappingPayload,
      }));
    }
    closePanelSoon();
  }, [
    ensureTablesViewForDrilldown,
    gridTab,
    numPmidColumnForPublications?.header,
    onClosePanel,
    row,
    scoreYearRange,
    searchItems,
    setMappingContent,
    tab,
    useDrillDownMapping,
    view,
  ]);

  const showScoreChart =
    breakdown != null && scoreYearRange != null && scoreYearRange.min < scoreYearRange.max;

  if (!assoc) {
    return (
      <p className="text-sm text-gray-500 px-1 py-2">
        Historical score is only available for GDA rows with complete association data.
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-2 sm:gap-3">
      <div className="flex min-h-0 flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-4">
        <aside className="flex min-h-0 w-full shrink-0 flex-col rounded-xl border border-slate-200 bg-white p-2 sm:p-3 lg:w-[min(100%,22rem)] lg:max-w-sm">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Year selection
          </p>
          <div className="min-h-0 max-h-[min(40vh,300px)] flex-1 overflow-y-auto lg:max-h-none">
            <ScoreHistoryPanel
              panel={panel}
              onUpdate={setPanel}
              hideHeader
              onSelectionCommit={handleSelectionCommit}
            />
          </div>
        </aside>

        <div
          className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white"
          style={{ minHeight: showScoreChart ? 'min(52vh, 420px)' : 260 }}
        >
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-2 py-2 sm:px-3">
            <h3 className="mb-0 text-xs font-semibold uppercase tracking-wide text-gray-800">
              Score for selection
            </h3>
          </div>
          <div
            className={`min-h-0 flex-1 p-2 sm:p-3 ${
              showScoreChart ? 'flex flex-col overflow-y-auto' : 'overflow-y-auto'
            }`}
          >
            {loading ? (
              <p className="mt-6 text-center text-sm text-gray-500">Loading…</p>
            ) : error ? (
              <p className="mt-6 text-center text-sm text-red-600">{error}</p>
            ) : breakdown ? (
              <div
                className={
                  showScoreChart
                    ? 'flex flex-1 flex-col gap-4 lg:min-h-0 lg:flex-row lg:items-stretch lg:gap-5'
                    : 'min-h-0'
                }
                style={showScoreChart ? { minHeight: 'min(40vh, 320px)' } : undefined}
              >
                <div className={showScoreChart ? 'min-h-0 min-w-0 w-full lg:w-2/5 lg:shrink-0' : 'w-full'}>
                  <ScoreBreakdown data={breakdown} showComponentDetailButton={false} />
                </div>
                {showScoreChart ? (
                  <div
                    className="flex w-full min-w-0 flex-col lg:w-3/5 lg:min-h-0 lg:flex-1 lg:border-l lg:border-gray-200 lg:pl-4"
                    style={{ minHeight: 'min(36vh, 300px)' }}
                  >
                    <HistoricalScoreLineChart
                      points={chartPoints}
                      loading={loading}
                      rangeLabel={scoreYearRange ? `${scoreYearRange.min}–${scoreYearRange.max}` : undefined}
                    />
                  </div>
                ) : null}
              </div>
            ) : noBreakdownInResponse ? (
              <p className="mt-6 text-center text-sm text-gray-400">No score breakdown available.</p>
            ) : (
              <p className="mt-6 text-center text-sm text-gray-400">
                Choose a year or range and apply to see the score breakdown.
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-3">
            <small className="text-gray-600">
              More about the score breakdown{' '}
              <Link to="/docs?section=metrics&subsection=historicalScore" className="text-blue-700 underline">
                in the docs
              </Link>
              .
            </small>
            <button
              type="button"
              className="shrink-0 rounded-md border border-pink-200 bg-white px-3 py-1.5 text-xs font-semibold text-pink-800 shadow-sm transition hover:bg-pink-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!scoreYearRange}
              onClick={handleViewPublications}
            >
              View in evidence
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
